import type { BlogPost } from "../../lib/blog";

export const pereNoelSecretEnFamille: BlogPost = {
  h1: "Père Noël secret en famille : enfants, couples, grands-parents, comment tout prévoir",
  seoTitle:
    "Père Noël secret en famille : le tirage avec enfants, couples et grands-parents · Listou",
  seoDescription:
    "Organiser un Père Noël secret en famille : faire participer les enfants, exclure les couples, inclure ceux qui n'ont pas d'email, gérer deux familles et deux réveillons, choisir le budget. Le guide pratique.",
  publishedOn: "2026-10-20",
  updatedOn: "2026-10-20",
  summary:
    "En famille, le Père Noël secret remplace la pile de cadeaux par un seul cadeau par personne, choisi avec soin. Il faut prévoir trois choses que le bureau ne demande pas : exclure les couples et les parents de leurs enfants, faire participer les enfants en âge de garder un secret (vers 6 ou 7 ans), et inclure ceux qui n'ont pas d'email. Les plus jeunes gardent leurs cadeaux habituels, hors tirage.",
  body: `
## Pourquoi passer au Père Noël secret en famille

À partir d'une dizaine de personnes au réveillon, l'échange général devient lourd : chacun achète dix cadeaux, souvent à la hâte, et la soirée se passe à déballer. Le Père Noël secret remplace tout cela par **un cadeau par personne**, pour lequel on a le temps de réfléchir, et un budget plus généreux que dix petits cadeaux.

L'idée se propose tôt, dès novembre, et de préférence par celui ou celle qui reçoit, pour que personne ne la prenne comme une économie imposée. Notre guide des [règles et du budget](/blog/pere-noel-secret-regles-budget) rappelle le fonctionnement général ; voici ce qui change en famille.

## Les exclusions : couples, parents et enfants

En famille, le tirage doit écarter ceux qui s'offrent déjà un cadeau :

- **les couples**, qui ont leur propre échange ;
- **les parents et leurs enfants**, pour la même raison ;
- **parfois les frères et sœurs** qui vivent sous le même toit.

Sur [Listou](/secret-santa), on colle la même gommette de couleur sur les membres d'un même foyer : ils ne se tirent pas entre eux. Si les règles rendent le tirage impossible, par exemple quand un seul foyer compte la moitié du groupe, Listou le signale avant d'envoyer quoi que ce soit.

Ne poussez pas les exclusions trop loin. Au-delà de deux ou trois foyers exclus pour chacun, le tirage devient prévisible, et la devinette du jour J perd son intérêt.

## Les enfants

### Les tout-petits, hors tirage

Jusqu'à 5 ou 6 ans, un enfant ne garde pas un secret et ne comprend pas qu'il ne recevra qu'un cadeau. Laissez-les en dehors : ils reçoivent leurs cadeaux habituels de leurs parents, et parfois des grands-parents, à côté du jeu.

### À partir de 6 ou 7 ans, avec un adulte

Un enfant de cet âge adore être le Père Noël de quelqu'un. Il participe au tirage, et un parent l'aide à choisir et à payer. Deux règles :

- **Le parent connaît le tirage de son enfant.** C'est inévitable, et c'est une raison de plus pour exclure les parents de leurs enfants.
- **L'enfant ne tombe pas sur un autre enfant de son âge** si l'on veut éviter les comparaisons. Une gommette commune pour les cousins règle la question.

### Les adolescents, comme les adultes

Ils participent seuls, avec le même budget que les autres ou un budget réduit qu'ils financent eux-mêmes. Annoncez-le clairement avant le tirage.

## Ceux qui n'ont pas d'email

Une grand-mère sans adresse email, un enfant, un oncle qui ne lit jamais ses messages : ils participent quand même. Sur Listou, l'email est facultatif. Ceux qui n'en donnent pas retrouvent leur prénom sur la page du groupe et découvrent leur tirage en un clic, depuis le téléphone d'un proche si besoin. Personne n'a de compte à créer.

Pour un grand-parent peu à l'aise, le plus simple est souvent qu'un petit-enfant lui montre son tirage et l'aide à lire la liste d'envies. Ce petit-enfant connaîtra le tirage : choisissez quelqu'un qui n'est pas concerné, ou acceptez-le.

## Le budget en famille

Il dépend de ce que le Père Noël secret remplace :

- **Il remplace tous les cadeaux entre adultes** : 40 à 60 €. Chacun dépense moins qu'avant, mais offre un vrai cadeau.
- **Il s'ajoute aux cadeaux habituels** : 20 à 30 €, comme entre amis.
- **Les enfants qui participent** : le même budget, payé par leurs parents, ou la moitié si l'on préfère.

Dites si c'est un montant à viser ou un plafond. Notre guide des [idées cadeaux par budget](/blog/idees-cadeaux-secret-santa) donne des pistes pour chaque palier.

## Deux familles, deux réveillons

Beaucoup de couples passent le 24 d'un côté et le 25 de l'autre. Deux solutions :

- **Deux tirages séparés**, un par famille. C'est le plus simple : chacun ne participe qu'au tirage des réveillons où il sera présent.
- **Un tirage commun** si les deux familles se retrouvent. Il faut alors une date d'échange que tout le monde connaît.

Évitez d'inscrire quelqu'un à un tirage pour une soirée où il ne sera pas : son cadeau voyagerait par la poste, et celui qu'il doit offrir aussi.

## La liste d'envies, surtout pour les enfants

En famille, on croit connaître tout le monde, et c'est là que les cadeaux ratent : l'oncle reçoit le même livre que l'an dernier, l'adolescente un pull qui n'est plus de son âge. Demandez à chacun quelques envies dans le budget. Sur Listou, la personne qui vous a tiré voit votre liste en ouvrant son tirage.

Pour les enfants, les parents remplissent la liste avec eux. C'est aussi l'occasion de glisser une taille de vêtement ou un jeu déjà possédé à éviter.

## Le jour J

Les cadeaux s'ouvrent l'un après l'autre, pas tous en même temps : c'est le seul moment du réveillon où toute la famille regarde la même chose. Commencez par les enfants, qui ne tiendront pas jusqu'au dessert.

Après l'ouverture, chacun devine son Père Noël. En famille, les indices sont faciles à trouver (l'écriture sur l'étiquette, le papier cadeau de la tante qui utilise toujours le même), et c'est ce qui rend la devinette amusante.

## Et l'année suivante

Le risque, au bout de deux ou trois Noëls, est de retomber sur les mêmes personnes. Listou garde le groupe, ses gommettes et ses règles d'une année sur l'autre, et écarte les tirages de l'an passé : il suffit d'ajouter les nouveaux venus et de relancer le tirage.
`,
  faq: [
    [
      "À partir de quel âge un enfant peut-il participer au Père Noël secret ?",
      "Vers 6 ou 7 ans, quand il peut garder un secret et comprend qu'il ne recevra qu'un cadeau du jeu. Un parent l'aide à choisir et à payer. Les plus petits restent hors tirage et reçoivent leurs cadeaux habituels.",
    ],
    [
      "Comment éviter que les couples se tirent au sort en famille ?",
      "Avec des exclusions. Sur Listou, on colle la même gommette de couleur sur les membres d'un même foyer : ils ne se tirent pas entre eux. Listou prévient si les règles rendent le tirage impossible.",
    ],
    [
      "Peut-on participer sans adresse email ?",
      "Oui. Sur Listou, l'email est facultatif : chacun peut retrouver son prénom sur la page du groupe et découvrir son tirage, sans créer de compte.",
    ],
    [
      "Quel budget pour un Père Noël secret en famille ?",
      "De 40 à 60 € s'il remplace tous les cadeaux entre adultes, de 20 à 30 € s'il s'ajoute aux cadeaux habituels. Les enfants qui participent suivent le même budget, payé par leurs parents, ou la moitié.",
    ],
    [
      "Comment faire quand on passe Noël dans deux familles ?",
      "Le plus simple est de faire deux tirages séparés, un par famille, et de ne participer qu'à ceux des réveillons où l'on sera présent.",
    ],
    [
      "Comment ne pas retomber sur la même personne chaque année ?",
      "Listou garde le groupe et ses règles d'une année sur l'autre et écarte les tirages de l'an passé. Il suffit d'ajouter les nouveaux venus et de relancer.",
    ],
  ],
  cta: "secret_santa",
};
